import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { collection, doc, getDoc, getDocs, query, where } from 'firebase/firestore';
import { Layout } from '../components/layout/Layout';
import { useCustomerAuth } from '../context/CustomerAuthContext';
import { signOutCustomer } from '../utils/customerAuth';
import { getOrders } from '../utils/adminStorage';
import { db } from '../firebase';

const SHIPPING_METHOD_LABELS = {
  gls_home: 'GLS házhozszállítás',
  gls_parcel_locker: 'GLS csomagautomata',
  packeta_parcel_locker: 'Packeta csomagpont/csomagautomata',
  personal_pickup_omega: 'Személyes átvétel (Omega)',
};
const PROFILE_BACKGROUND = '/hero-lumenverde-bg.png';

const DEFAULT_VISIBLE_ORDER_STATUSES = new Set(['new', 'packed']);

const ORDERS_UPDATED_EVENT = 'lumenverde-orders-updated';

const formatTimestamp = (value) => {
  if (!value) return '—';
  if (typeof value === 'object' && typeof value.toDate === 'function') {
    return value.toDate().toLocaleString('hu-HU');
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '—';
  return parsed.toLocaleString('hu-HU');
};

const toMillis = (value) => {
  if (!value) return null;
  if (typeof value === 'object' && typeof value.toDate === 'function') {
    return value.toDate().getTime();
  }
  if (typeof value === 'object' && typeof value.toMillis === 'function') {
    return value.toMillis();
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed.getTime();
};

const getOrderMillis = (order) => toMillis(order?.createdAtServer) ?? toMillis(order?.createdAt) ?? 0;

const normalizeOrderStatus = (status) =>
  typeof status === 'string' && status.trim() ? status.trim().toLowerCase() : 'new';

const normalizeShippingKey = (shippingMethod) =>
  String(shippingMethod || '')
    .trim()
    .toLowerCase();

const isPersonalPickupShipping = (shippingMethod) => {
  const key = normalizeShippingKey(shippingMethod);
  return key === 'personal_pickup_omega' || key.includes('personal_pickup') || key.includes('szemelyes');
};

const getOrderStatusLabel = (order) => {
  const status = normalizeOrderStatus(order?.status);
  if (status === 'shipped') {
    return isPersonalPickupShipping(order?.shipping) ? 'Átvehető' : 'Kiszállítás alatt';
  }
  return 'Feldolgozás alatt';
};

const matchesUserOrder = (order, userUid, userEmail) => {
  if (!order || !userUid) return false;
  if (typeof order.customerUid === 'string' && order.customerUid === userUid) return true;
  const orderEmail = String(order?.customer?.email || '').trim().toLowerCase();
  const normalizedUserEmail = String(userEmail || '').trim().toLowerCase();
  return Boolean(orderEmail && normalizedUserEmail && orderEmail === normalizedUserEmail);
};

const mergeAndSortOrders = (...lists) => {
  const merged = new Map();
  lists.flat().forEach((order) => {
    const key = String(order?.id || order?.orderNumber || '').trim();
    if (!key) return;
    const existing = merged.get(key);
    if (!existing || getOrderMillis(order) >= getOrderMillis(existing)) {
      merged.set(key, order);
    }
  });
  return Array.from(merged.values()).sort((a, b) => getOrderMillis(b) - getOrderMillis(a));
};

const getOrderKey = (order) => String(order?.id || order?.orderNumber || '').trim();

const getOrderItems = (order) => (Array.isArray(order?.items) ? order.items : []);

const getOrderItemName = (item) =>
  item?.name || item?.product?.name || item?.title || item?.sku || 'Termék';

const getOrderItemQuantity = (item) => {
  const rawQuantity = Number(item?.quantity ?? item?.qty ?? item?.count ?? 1);
  if (!Number.isFinite(rawQuantity) || rawQuantity <= 0) return 1;
  return rawQuantity;
};

const getOrderItemUnitPrice = (item) => {
  const rawPrice = Number(item?.price ?? item?.unitPrice ?? item?.product?.price ?? 0);
  if (!Number.isFinite(rawPrice) || rawPrice < 0) return 0;
  return rawPrice;
};

export default function Profile() {
  const { user, loading } = useCustomerAuth();
  const userUid = user?.uid || '';
  const userEmail = typeof user?.email === 'string' ? user.email.trim() : '';
  const [profile, setProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(true);
  const [orders, setOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [error, setError] = useState('');
  const [signingOut, setSigningOut] = useState(false);
  const [activeView, setActiveView] = useState('orders');
  const [showAllOrders, setShowAllOrders] = useState(false);
  const [expandedOrderKeys, setExpandedOrderKeys] = useState({});

  useEffect(() => {
    if (!user?.uid) {
      setProfile(null);
      setProfileLoading(false);
      return undefined;
    }

    let isCancelled = false;

    const loadProfile = async () => {
      setProfileLoading(true);
      setError('');
      try {
        const profileSnap = await getDoc(doc(db, 'customerProfiles', user.uid));
        if (isCancelled) return;
        setProfile(profileSnap.exists() ? profileSnap.data() : null);
      } catch {
        if (isCancelled) return;
        setError('Nem sikerült betölteni a profil adatokat.');
      } finally {
        if (!isCancelled) setProfileLoading(false);
      }
    };

    loadProfile();
    return () => {
      isCancelled = true;
    };
  }, [user?.uid]);

  useEffect(() => {
    if (!userUid) {
      setOrders([]);
      setOrdersLoading(false);
      return undefined;
    }

    let isCancelled = false;

    const loadOrders = async () => {
      setOrdersLoading(true);
      try {
        const firestoreOrders = [];
        const orderQueries = [getDocs(query(collection(db, 'orders'), where('customerUid', '==', userUid)))];
        if (userEmail) {
          orderQueries.push(
            getDocs(query(collection(db, 'orders'), where('customer.email', '==', userEmail))),
          );
        }

        const querySnapshots = await Promise.allSettled(orderQueries);
        querySnapshots.forEach((result) => {
          if (result.status !== 'fulfilled') return;
          result.value.docs.forEach((snap) => {
            firestoreOrders.push({ id: snap.id, ...snap.data() });
          });
        });

        const localOrders = getOrders().filter((order) => matchesUserOrder(order, userUid, userEmail));
        if (isCancelled) return;
        setOrders(mergeAndSortOrders(firestoreOrders, localOrders));
      } catch {
        if (isCancelled) return;
        const localOrders = getOrders().filter((order) => matchesUserOrder(order, userUid, userEmail));
        setOrders(mergeAndSortOrders(localOrders));
      } finally {
        if (!isCancelled) setOrdersLoading(false);
      }
    };

    loadOrders();

    const handleOrdersUpdated = () => {
      loadOrders();
    };

    window.addEventListener(ORDERS_UPDATED_EVENT, handleOrdersUpdated);
    window.addEventListener('focus', handleOrdersUpdated);
    return () => {
      isCancelled = true;
      window.removeEventListener(ORDERS_UPDATED_EVENT, handleOrdersUpdated);
      window.removeEventListener('focus', handleOrdersUpdated);
    };
  }, [userUid, userEmail]);

  const shippingMethodLabel = useMemo(() => {
    const method = profile?.shipping?.shippingMethod;
    if (!method) return '—';
    return SHIPPING_METHOD_LABELS[method] || method;
  }, [profile]);

  const defaultOrders = useMemo(
    () => orders.filter((order) => DEFAULT_VISIBLE_ORDER_STATUSES.has(normalizeOrderStatus(order?.status))),
    [orders],
  );

  const displayedOrders = showAllOrders ? orders : defaultOrders;
  const hasHiddenOrders = !showAllOrders && orders.length > defaultOrders.length;

  const toggleOrderDetails = (orderKey) => {
    if (!orderKey) return;
    setExpandedOrderKeys((prev) => ({
      ...prev,
      [orderKey]: !prev[orderKey],
    }));
  };

  const handleSignOut = async () => {
    setSigningOut(true);
    setError('');
    try {
      await signOutCustomer();
    } catch {
      setError('Nem sikerült kijelentkezni.');
    } finally {
      setSigningOut(false);
    }
  };

  return (
    <Layout>
      <div className="section-padding relative overflow-hidden">
        <div className="absolute inset-0" aria-hidden="true">
          <div
            className="h-full w-full bg-cover bg-center bg-no-repeat"
            style={{ backgroundImage: `url(${PROFILE_BACKGROUND})` }}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-background/70 via-background/40 to-background/85" />
        </div>

        <div className="container-zinga relative z-10 max-w-3xl">
          <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
            <h1 className="font-display text-3xl md:text-4xl font-bold">Profil</h1>
            {user ? (
              <button
                type="button"
                onClick={handleSignOut}
                disabled={signingOut}
                className="btn-secondary text-sm"
              >
                {signingOut ? 'Kijelentkezés...' : 'Kijelentkezés'}
              </button>
            ) : null}
          </div>

          {loading ? (
            <div className="rounded-3xl border border-border/70 bg-card p-8">
              <p className="text-sm text-muted-foreground">Bejelentkezési állapot ellenőrzése...</p>
            </div>
          ) : !user ? (
            <div className="rounded-3xl border border-border/70 bg-card p-8 space-y-4">
              <p className="text-sm text-muted-foreground">
                A profil eléréséhez jelentkezz be.
              </p>
              <Link to="/bejelentkezes" className="btn-primary text-sm inline-flex">
                Bejelentkezés
              </Link>
            </div>
          ) : profileLoading ? (
            <div className="rounded-3xl border border-border/70 bg-card p-8">
              <p className="text-sm text-muted-foreground">Profil adatok betöltése...</p>
            </div>
          ) : (
            <div className="space-y-6">
              <div className="rounded-3xl border border-border/70 bg-card p-6">
                <p className="text-sm font-medium">{user.displayName || user.email || user.uid}</p>
                <p className="text-xs text-muted-foreground mt-1">
                  Utolsó mentés: {formatTimestamp(profile?.updatedAt)}
                </p>
              </div>

              <div className="rounded-3xl border border-border/70 bg-card p-2">
                <div className="grid gap-2 sm:grid-cols-2">
                  <button
                    type="button"
                    onClick={() => setActiveView('orders')}
                    className={`rounded-2xl px-4 py-3 text-sm font-medium transition-colors ${
                      activeView === 'orders'
                        ? 'bg-[#0F3D2C] text-[#F1E6D3]'
                        : 'bg-secondary text-foreground hover:bg-secondary/70'
                    }`}
                  >
                    Rendelések
                  </button>
                  <button
                    type="button"
                    onClick={() => setActiveView('personal')}
                    className={`rounded-2xl px-4 py-3 text-sm font-medium transition-colors ${
                      activeView === 'personal'
                        ? 'bg-[#0F3D2C] text-[#F1E6D3]'
                        : 'bg-secondary text-foreground hover:bg-secondary/70'
                    }`}
                  >
                    Személyes adatok
                  </button>
                </div>
              </div>

              {activeView === 'orders' ? (
                <div className="rounded-3xl border border-border/70 bg-card p-6">
                  <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                    <h2 className="font-display text-xl font-semibold">Rendelések</h2>
                    {orders.length > defaultOrders.length ? (
                      <button
                        type="button"
                        onClick={() => setShowAllOrders((prev) => !prev)}
                        className="btn-secondary text-xs"
                      >
                        {showAllOrders ? 'Csak feldolgozás alatt' : 'Összes rendelés'}
                      </button>
                    ) : null}
                  </div>
                  {ordersLoading ? (
                    <p className="text-sm text-muted-foreground">Rendelések betöltése...</p>
                  ) : displayedOrders.length ? (
                    <div className="space-y-3">
                      {displayedOrders.map((order) => {
                        const orderKey = getOrderKey(order);
                        const orderItems = getOrderItems(order);
                        const isExpanded = Boolean(expandedOrderKeys[orderKey]);

                        return (
                          <div
                            key={orderKey}
                            className="rounded-2xl border border-border/60 bg-background/60 p-4"
                          >
                          <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                            <div className="min-w-0">
                              <p className="font-medium break-words">Rendelésszám: {order.orderNumber || '—'}</p>
                              <p className="text-xs text-muted-foreground">
                                Leadva: {formatTimestamp(order.createdAtServer || order.createdAt)}
                              </p>
                              <p className="text-xs text-muted-foreground mt-1">
                                Státusz: {getOrderStatusLabel(order)}
                              </p>
                            </div>
                            <div className="text-sm font-semibold shrink-0">
                              {Number(order.total || 0).toLocaleString('hu-HU')} Ft
                            </div>
                          </div>
                          {orderKey ? (
                            <div className="mt-3">
                              <button
                                type="button"
                                onClick={() => toggleOrderDetails(orderKey)}
                                className="text-sm underline underline-offset-2 hover:text-foreground/80"
                              >
                                {isExpanded ? 'Tételek elrejtése' : 'Tételek megtekintése'}
                              </button>
                            </div>
                          ) : null}
                          {isExpanded ? (
                            <div className="mt-3 rounded-xl border border-border/60 bg-card/80 p-3">
                              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                                Rendelés tételei
                              </p>
                              {orderItems.length ? (
                                <div className="mt-2 space-y-2">
                                  {orderItems.map((item, itemIndex) => {
                                    const quantity = getOrderItemQuantity(item);
                                    const unitPrice = getOrderItemUnitPrice(item);
                                    const lineTotal = quantity * unitPrice;

                                    return (
                                      <div
                                        key={`${orderKey}-item-${itemIndex}`}
                                        className="flex flex-col gap-1 rounded-lg border border-border/50 bg-background/70 p-2 text-sm sm:flex-row sm:items-center sm:justify-between"
                                      >
                                        <div className="min-w-0">
                                          <p className="font-medium break-words">{getOrderItemName(item)}</p>
                                          <p className="text-xs text-muted-foreground">
                                            {quantity} db × {unitPrice.toLocaleString('hu-HU')} Ft
                                          </p>
                                        </div>
                                        <p className="text-sm font-semibold shrink-0">
                                          {lineTotal.toLocaleString('hu-HU')} Ft
                                        </p>
                                      </div>
                                    );
                                  })}
                                </div>
                              ) : (
                                <p className="mt-2 text-sm text-muted-foreground">
                                  Ehhez a rendeléshez nem található tétellista.
                                </p>
                              )}
                            </div>
                          ) : null}
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <p className="text-sm text-muted-foreground">
                        {hasHiddenOrders
                          ? 'Jelenleg nincs feldolgozás alatt lévő rendelés.'
                          : 'Jelenleg nincs rendelés ehhez a profilhoz.'}
                      </p>
                      {hasHiddenOrders ? (
                        <button type="button" onClick={() => setShowAllOrders(true)} className="btn-secondary text-xs">
                          Összes rendelés megjelenítése
                        </button>
                      ) : null}
                    </div>
                  )}
                </div>
              ) : (
                <>
                  <div className="rounded-3xl border border-border/70 bg-card p-6">
                    <h2 className="font-display text-xl font-semibold mb-4">Kapcsolattartási adatok</h2>
                    {profile?.contact ? (
                      <div className="grid gap-2 text-sm">
                        <p>Név: {profile.contact.fullName || '—'}</p>
                        <p>Email: {profile.contact.email || '—'}</p>
                        <p>Telefon: {profile.contact.phone || '—'}</p>
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground">
                        Még nincs mentett kapcsolattartási adat.
                      </p>
                    )}
                  </div>

                  <div className="rounded-3xl border border-border/70 bg-card p-6">
                    <h2 className="font-display text-xl font-semibold mb-4">Szállítási adatok</h2>
                    {profile?.shipping ? (
                      <div className="grid gap-2 text-sm">
                        <p>Cím: {profile.shipping.address || '—'}</p>
                        <p>Város: {profile.shipping.city || '—'}</p>
                        <p>Irányítószám: {profile.shipping.zip || '—'}</p>
                        <p>Ország: {profile.shipping.country || '—'}</p>
                        <p>Szállítás: {shippingMethodLabel}</p>
                        {profile.shipping.pickupPoint?.name ? (
                          <p>Átvételi pont: {profile.shipping.pickupPoint.name}</p>
                        ) : null}
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground">
                        Még nincs mentett szállítási adat.
                      </p>
                    )}
                  </div>

                  <div className="rounded-3xl border border-border/70 bg-card p-6">
                    <h2 className="font-display text-xl font-semibold mb-4">Számlázási adatok</h2>
                    {profile?.billing?.useDifferentBilling ? (
                      <div className="grid gap-2 text-sm">
                        <p>Cím: {profile.billing.address || '—'}</p>
                        <p>Város: {profile.billing.city || '—'}</p>
                        <p>Irányítószám: {profile.billing.zip || '—'}</p>
                        <p>Ország: {profile.billing.country || '—'}</p>
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground">
                        Külön számlázási cím nincs megadva.
                      </p>
                    )}
                  </div>

                  <div className="flex justify-end">
                    <Link to="/checkout" className="btn-primary text-sm inline-flex">
                      Adatok frissítése a pénztárban
                    </Link>
                  </div>
                </>
              )}
            </div>
          )}

          {error ? (
            <div className="mt-4 rounded-xl border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
              {error}
            </div>
          ) : null}
        </div>
      </div>
    </Layout>
  );
}
